'use client';

import { useEffect, useRef } from 'react';
import { useSession } from 'next-auth/react';
import { useTelegramMiniApp } from '../hooks/useTelegramMiniApp';

/**
 * TelegramMiniAppAuth
 *
 * Seamless Mini App login. When running unauthenticated inside a Telegram Mini
 * App, POST the Telegram `initData` to the portal Mini App auth route, which
 * delegates identity to central accounts.owlcat (verify HMAC → identity by
 * tgId → JIT portal member → native session + shared owlcat-id cookie). No
 * widget, no popup — the user is authenticated silently from the signed initData.
 *
 * Fallbacks: if there is no initData, or the S2S login fails, bounce to the
 * portal /auth/signin route (which server-redirects to accounts /login) so the
 * user can still sign in via the central widget. One-shot per mount.
 */
export function TelegramMiniAppAuth() {
  const { isMiniApp, isReady, initData } = useTelegramMiniApp();
  const { status } = useSession();

  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    if (!isReady || !isMiniApp) return;
    if (status !== 'unauthenticated') return;
    hasRun.current = true;

    const bounceToLogin = () => {
      const locale = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/)?.[1] ?? 'ru';
      const cb = window.location.pathname + window.location.search;
      window.location.href = `/${locale}/auth/signin?callbackUrl=${encodeURIComponent(cb)}`;
    };

    // No signed initData available → fall back to the central widget login.
    if (!initData) {
      bounceToLogin();
      return;
    }

    // Seamless: exchange initData for a portal session via accounts (S2S).
    void (async () => {
      try {
        const res = await fetch('/api/auth/telegram/miniapp', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ initData }),
        });
        if (!res.ok) {
          bounceToLogin();
          return;
        }
        // Session cookie is now set — reload so the app reads the new session.
        window.location.reload();
      } catch {
        bounceToLogin();
      }
    })();
  }, [isMiniApp, isReady, initData, status]);

  return null;
}
